import { useState } from 'react';
import { motion } from 'framer-motion';
import { MediaGallery } from '../../../components/MediaGallery';

export function VehiclesSection() {
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const themeGalleries = [
    {
      title: '소규모 프라이빗 투어',
      subtitle: '2~6명 | 스타리아 차량',
      description: '편안한 이동, 여유 있는 일정, 가족·연인·소규모 여행에 최적화',
      icon: '🚗',
      color: 'bg-gradient-to-br from-orange-400 to-orange-600'
    },
    {
      title: '단체 맞춤 투어',
      subtitle: '10~25명 | 29인승 버스',
      description: '상담부터 운전, 현장 진행까지 대표가 전 일정 직접 책임',
      icon: '🚌',
      color: 'bg-gradient-to-br from-teal-400 to-teal-600'
    },
    {
      title: '특별 서비스',
      subtitle: '사진·영상 촬영 & 편집',
      description: '여행은 끝나도 추억은 남도록 전문 감성으로 기록',
      icon: '📸',
      color: 'bg-gradient-to-br from-pink-400 to-pink-600'
    },
    {
      title: '자연 체험 투어',
      subtitle: '한라산 등반 & 올레길',
      description: '제주에서만 즐길 수 있는 자연과 함께하는 특별한 경험',
      icon: '🏔️',
      color: 'bg-gradient-to-br from-emerald-400 to-emerald-600'
    },
    {
      title: '힐링 & 감성 여행',
      subtitle: '자유 여행 & 웨딩 스냅',
      description: '나만의 제주 감성 여행과 소중한 순간을 특별하게',
      icon: '🌸',
      color: 'bg-gradient-to-br from-indigo-400 to-indigo-600'
    },
    {
      title: '전문 촬영 서비스',
      subtitle: 'CF & 방송 촬영',
      description: '전문 촬영 지원 및 제작 서비스',
      icon: '🎬',
      color: 'bg-gradient-to-br from-red-400 to-red-600'
    },
    {
      title: '레저 & 스포츠',
      subtitle: '골프 부킹 & 워크숍',
      description: '프리미엄 골프장 예약 및 단체 행사 전문',
      icon: '⛳',
      color: 'bg-gradient-to-br from-green-400 to-green-600'
    },
    {
      title: '특별한 날 서비스',
      subtitle: '결혼식 픽업',
      description: '소중한 날, 안전하고 품격 있는 이동 지원',
      icon: '💐',
      color: 'bg-gradient-to-br from-rose-400 to-rose-600'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50" id="vehicles">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-3 bg-[#0A7B83]/10 px-6 py-3 rounded-full mb-6">
            <span className="text-2xl">🚗</span>
            <span className="text-[#0A7B83] font-semibold text-sm uppercase tracking-wide">
              제주블리여행사 테마별 갤러리
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-6 leading-tight">
            8개 테마별 여행 갤러리
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            각 테마를 클릭하여 여행 사진, 동영상, 후기를 확인하고 관리하세요
          </p>
        </motion.div>

        {/* 8 Theme Galleries Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {themeGalleries.map((gallery, index) => (
            <motion.div
              key={gallery.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`${gallery.color} rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer`}
              style={{
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.2), 0 6px 12px rgba(0, 0, 0, 0.1)'
              }}
              onClick={() => setSelectedService(gallery.title)}
            >
              <div className="text-5xl mb-4 text-center">{gallery.icon}</div>
              <h3 className="text-xl font-bold mb-2 text-center">{gallery.title}</h3>
              <p className="text-sm text-white/90 text-center mb-3 font-medium">
                {gallery.subtitle}
              </p>
              <p className="text-xs text-white/80 text-center leading-relaxed mb-4">
                {gallery.description}
              </p>
              <div className="mt-4 text-center">
                <span className="text-xs bg-white/20 px-3 py-1.5 rounded-full inline-flex items-center gap-1">
                  <i className="ri-gallery-line"></i>
                  갤러리 보기
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-3xl p-8 shadow-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              문의 및 예약
            </h3>
            <p className="text-gray-600 mb-6">
              각 테마별 자세한 정보와 예약은 상담을 통해 확인하세요
            </p>
            <button
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
              className="bg-[#FEE500] hover:bg-[#FEE500]/90 text-[#3C1E1E] px-8 py-4 rounded-2xl font-bold text-lg transition-all hover:scale-105 shadow-lg whitespace-nowrap"
            >
              💬 상담하기
            </button>
          </div>
        </motion.div>
      </div>

      {/* Media Gallery Modal */}
      {selectedService && (
        <MediaGallery
          serviceTitle={selectedService}
          isOpen={!!selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}
    </section>
  );
}
